import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useMemo, useState, type KeyboardEvent } from 'react';

import cn from '@/utils/cn';

import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  // When provided, a non-empty search that matches no option shows an "Add" row
  // that calls this with the typed value (used to persist a brand-new city).
  onCreate?: (value: string) => void;
  // Set false when rendered inside a modal Dialog so the list stays scrollable.
  portalled?: boolean;
  // Options that stay VISIBLE but cannot be picked - e.g. a value already used by a
  // sibling control. Greying beats filtering them out: an option that vanishes reads
  // as missing data, one that is greyed reads as taken.
  disabledOptions?: readonly string[];
  // Overrides the trigger height, which defaults to the form-field h-9.
  className?: string;
}

export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  onCreate,
  portalled = true,
  disabledOptions,
  className,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const trimmed = query.trim();
  const canCreate =
    !!onCreate &&
    trimmed.length > 0 &&
    !options.some((o) => o.toLowerCase() === trimmed.toLowerCase());

  const select = (next: string): void => {
    onChange(next);
    setQuery('');
    setOpen(false);
  };

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    // The first SELECTABLE match, not simply the first: Enter used to commit a value
    // the list itself was showing as unavailable.
    const first = filtered.find((option) => !disabledOptions?.includes(option));
    if (first !== undefined) {
      select(first);
    } else if (canCreate) {
      onCreate?.(trimmed);
      select(trimmed);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-between rounded-lg font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        portalled={portalled}
        align="start"
        className="w-[var(--radix-popover-trigger-width)] rounded-lg bg-white p-0"
      >
        <div className="border-b border-border p-2">
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- focus the search on open */}
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search…"
            className="h-8"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.map((option) => {
            const isDisabled = disabledOptions?.includes(option) ?? false;
            return (
              <button
                key={option}
                type="button"
                disabled={isDisabled}
                onClick={() => select(option)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                  // Matches the Radix Select rows: pale accent on hover, brand
                  // fill on the chosen option. The selected style is listed
                  // last so it wins - these are plain classes on one element,
                  // so the later one in the merged string takes effect.
                  isDisabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-accent',
                  value === option &&
                    'bg-primary font-semibold text-primary-foreground hover:bg-brand-hover',
                )}
              >
                <Check
                  className={cn('h-4 w-4 shrink-0', value === option ? 'opacity-100' : 'opacity-0')}
                />
                <span className="truncate">{option}</span>
              </button>
            );
          })}
          {canCreate && (
            <button
              type="button"
              onClick={() => {
                onCreate?.(trimmed);
                select(trimmed);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-primary hover:bg-accent"
            >
              <Plus className="h-4 w-4 shrink-0" />
              <span className="truncate">{`Add "${trimmed}"`}</span>
            </button>
          )}
          {filtered.length === 0 && !canCreate && (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">No results.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
